import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Eye, EyeOff, Mail, Lock, AlertCircle, CheckCircle, Shield, ArrowLeft } from 'lucide-react';

type Step = 'email' | 'email_sent' | 'totp' | 'password' | 'success';

interface ForgotPasswordFlowProps {
  onBack: () => void;
  accentColor?: string;
  userType?: 'consultant' | 'client' | 'admin';
}

const LOGIN_ROUTES: Record<string, string> = {
  consultant: '/consultant/login',
  client: '/customer/login',
  admin: '/admin/login',
};

const LOGIN_LABELS: Record<string, string> = {
  consultant: 'Consultant Login',
  client: 'Client Login',
  admin: 'Admin Login',
};

function SuccessStep({ resolvedType, accentColor, onBack }: { resolvedType: string | null; accentColor: string; onBack: () => void }) {
  const loginRoute = resolvedType ? LOGIN_ROUTES[resolvedType] : null;
  const loginLabel = resolvedType ? (LOGIN_LABELS[resolvedType] ?? 'Login') : 'Login';
  return (
    <div className="text-center py-4">
      <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
        <CheckCircle className="w-10 h-10 text-green-600" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-3">Password Reset Successful</h2>
      <p className="text-gray-600 mb-8">Your password has been updated successfully.</p>
      <button
        type="button"
        onClick={() => { window.location.href = loginRoute ?? window.location.pathname; }}
        style={{ backgroundColor: accentColor }}
        className="w-full text-black font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity"
      >
        Go to {loginLabel}
      </button>
    </div>
  );
}

function validatePassword(password: string): string | null {
  if (password.length < 8) return 'Password must be at least 8 characters.';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter.';
  if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter.';
  if (!/[0-9]/.test(password)) return 'Password must contain at least one number.';
  if (!/[^A-Za-z0-9]/.test(password)) return 'Password must contain at least one special character (e.g. !@#$%).';
  return null;
}

export default function ForgotPasswordFlow({ onBack, accentColor = '#40E0D0', userType }: ForgotPasswordFlowProps) {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [detectedUserType, setDetectedUserType] = useState<string | null>(null);
  const resolvedType = userType ?? detectedUserType ?? null;

  const apiBase = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/password-reset-with-totp`;
  const headers = {
    'Content-Type': 'application/json',
    'Apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(apiBase, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email: email.trim(), step: 'validate-email', portalUserType: userType }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Unable to verify account. Please check your email address.');
        return;
      }

      if (data.emailSent) {
        // No 2FA on this account — standard reset email was sent
        setStep('email_sent');
        return;
      }

      if (data.userType) {
        setDetectedUserType(data.userType);
      }

      setStep('totp');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTotpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (totpCode.length !== 6) {
      setError('Please enter a 6-digit code.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(apiBase, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email: email.trim(), totpToken: totpCode, step: 'verify-totp', portalUserType: userType }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Invalid code. Please try again.');
        return;
      }

      setSessionToken(data.sessionToken);
      setStep('password');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const pwError = validatePassword(newPassword);
    if (pwError) { setError(pwError); return; }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(apiBase, {
        method: 'POST',
        headers: {
          ...headers,
          Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({ email: email.trim(), newPassword, step: 'reset-password' }),
      });

      const data = await res.json();

      if (!res.ok) {
        const msg: string = data.error || 'Failed to reset password. Please try again.';
        setError(msg.toLowerCase().includes('different') || msg.toLowerCase().includes('same')
          ? 'New password must be different from your current password.'
          : msg);
        return;
      }

      setStep('success');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTotpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 6) {
      setTotpCode(value);
      setError('');
    }
  };

  const inputClass = "w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-gray-900 bg-white";

  return (
    <div>
      {step !== 'success' && step !== 'email_sent' && (
        <button
          type="button"
          onClick={() => {
            if (step === 'totp') { setStep('email'); setTotpCode(''); setError(''); }
            else if (step === 'password') { setStep('totp'); setError(''); }
            else { onBack(); }
          }}
          className="flex items-center text-gray-500 hover:text-gray-800 text-sm transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {step === 'email' ? 'Back to Login' : 'Back'}
        </button>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-5 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {step === 'email' && (
        <>
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Reset Password</h2>
            <p className="text-gray-600 text-sm">Enter your email address to get started.</p>
          </div>
          <form onSubmit={handleEmailSubmit} className="space-y-5">
            <div>
              <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  id="reset-email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  className={inputClass}
                  placeholder="Enter your email"
                  required
                  autoFocus
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{ backgroundColor: accentColor }}
              className="w-full text-black font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Verifying...</>
              ) : 'Verify Account'}
            </button>
          </form>
        </>
      )}

      {step === 'email_sent' && (
        <div className="text-center py-4">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
            <Mail className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Check Your Email</h2>
          <p className="text-gray-600 mb-2">
            We've sent a password reset link to <strong>{email}</strong>.
          </p>
          <p className="text-gray-500 text-sm mb-8">
            Click the link in the email to choose a new password. Check your spam folder if you don't see it.
          </p>
          <button
            type="button"
            onClick={onBack}
            style={{ backgroundColor: accentColor }}
            className="w-full text-black font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity"
          >
            Back to Login
          </button>
        </div>
      )}

      {step === 'totp' && (
        <>
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-50 rounded-full mb-4">
              <Shield className="w-7 h-7 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Verify Your Identity</h2>
            <p className="text-gray-600 text-sm">Enter the 6-digit code from your authenticator app.</p>
          </div>
          <form onSubmit={handleTotpSubmit} className="space-y-5">
            <div>
              <label htmlFor="totp-code" className="block text-sm font-medium text-gray-700 mb-2 text-center">Authentication Code</label>
              <input
                type="text"
                id="totp-code"
                value={totpCode}
                onChange={handleTotpChange}
                placeholder="000000"
                className="w-full px-4 py-4 border border-gray-300 rounded-lg text-center text-3xl tracking-widest font-mono focus:outline-none focus:ring-2 text-gray-900 bg-white"
                maxLength={6}
                autoComplete="off"
                autoFocus
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting || totpCode.length !== 6}
              style={{ backgroundColor: accentColor }}
              className="w-full text-black font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Verifying...</>
              ) : 'Verify Account'}
            </button>
          </form>
          <p className="text-xs text-gray-500 text-center mt-4">
            Lost access?{' '}
            <a href="/request-access" className="text-blue-600 hover:underline">Contact support</a>
          </p>
        </>
      )}

      {step === 'password' && (
        <>
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Create New Password</h2>
            <p className="text-gray-600 text-sm">Choose a strong password for your account.</p>
          </div>
          <form onSubmit={handlePasswordSubmit} className="space-y-5">
            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="new-password"
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-gray-900 bg-white"
                  placeholder="Enter new password"
                  required
                  autoFocus
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Min 8 characters with uppercase, lowercase, number, and special character</p>
            </div>
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-gray-900 bg-white"
                  placeholder="Confirm new password"
                  required
                />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{ backgroundColor: accentColor }}
              className="w-full text-black font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Resetting Password...</>
              ) : 'Reset Password'}
            </button>
          </form>
        </>
      )}

      {step === 'success' && (
        <SuccessStep
          resolvedType={resolvedType}
          accentColor={accentColor}
          onBack={onBack}
        />
      )}
    </div>
  );
}
